import {
  SignupFormData,
  SignupFormLocation,
} from "@redotech/redo-marketing-common/schemas/submit-signup-form-step-schema";
import {
  CustomFieldType,
  FormElement,
  InputElement,
  MarketingForgeElementType,
} from "@redotech/redo-model/content-builder/element-schema";
import {
  CustomerCustomFieldDataType,
  CustomerCustomFieldValue,
} from "@redotech/redo-model/customer-custom-fields/customer-custom-fields-definition";
import { customFieldTypeToDataType } from "@redotech/redo-model/signup-form";
import { SpotlightForm } from "@redotech/redo-model/spotlight-schema";
import { useEffect, useState } from "react";
import { ANONYMOUS_SHOPPER_ID_KEY } from "../../utils/shared-conf";
import { SpotlightClient, SpotlightLocalData } from "./types";

export const EXPIRATION_PERIOD = 24 * 60 * 60 * 1000;

export function isOnMobile(): boolean {
  if ((navigator as any).userAgentData?.mobile) {
    return true;
  }
  if (
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent,
    )
  ) {
    return true;
  }
  return innerWidth < 768;
}

export function parseLocalData(data: string): SpotlightLocalData {
  try {
    const raw = JSON.parse(data);
    return {
      spotlightAutoOpenedAt: raw.spotlightAutoOpenedAt
        ? new Date(raw.spotlightAutoOpenedAt)
        : undefined,
      spotlightViewedAt: raw.spotlightViewedAt
        ? new Date(raw.spotlightViewedAt)
        : undefined,
      atLeastOneStepSubmittedAt: raw.atLeastOneStepSubmittedAt
        ? new Date(raw.atLeastOneStepSubmittedAt)
        : undefined,
      lastTriggeredAt: raw.lastTriggeredAt
        ? new Date(raw.lastTriggeredAt)
        : undefined,
      lastBackInStockTriggeredAt: raw.lastBackInStockTriggeredAt
        ? new Date(raw.lastBackInStockTriggeredAt)
        : undefined,
      lastBackInStockProductId: raw.lastBackInStockProductId,
      spotlightId: raw.spotlightId,
    };
  } catch (e) {
    console.error("Failed to parse spotlight localStorage", e);
    return {};
  }
}

function stepHasElementType(
  elements: readonly FormElement[],
  type: MarketingForgeElementType,
): boolean {
  for (const element of elements) {
    if (element.type === type) {
      return true;
    }
    if (
      (element.type === MarketingForgeElementType.COLUMN ||
        element.type === MarketingForgeElementType.CONTAINER) &&
      stepHasElementType(element.elements, type)
    ) {
      return true;
    }
  }
  return false;
}

function isCompletionStep(
  spotlight: SpotlightForm,
  stepIndex: number,
): boolean {
  const formHasAnyInput = spotlight.steps.some((step) =>
    stepHasElementType(step.elements, MarketingForgeElementType.INPUT),
  );

  const relevantType = formHasAnyInput
    ? MarketingForgeElementType.INPUT
    : MarketingForgeElementType.BUTTON;

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  if (!stepHasElementType(spotlight.steps[stepIndex].elements, relevantType)) {
    return false;
  }
  for (let j = stepIndex + 1; j < spotlight.steps.length; j++) {
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    if (stepHasElementType(spotlight.steps[j].elements, relevantType)) {
      return false;
    }
  }
  return true;
}

export const getNextStepIndex = (
  spotlight: SpotlightForm | null,
  startIndex: number,
): number => {
  if (!spotlight) {
    return startIndex;
  }
  // For spotlights, we don't skip steps - just advance to next
  return startIndex;
};

export const submitSpotlightStep = async ({
  i,
  formData,
  spotlight,
  spotlightId,
  treatmentId,
  client,
  customerId,
  shopifyCustomerId,
  setCurrentStep,
  location,
  onLastStepSubmitted,
}: {
  i: number;
  formData: Record<string, string>;
  spotlight: SpotlightForm | null;
  spotlightId: string | null;
  treatmentId: string | null;
  client: SpotlightClient | undefined;
  customerId?: string | null;
  shopifyCustomerId: string | null;
  setCurrentStep: (step: number) => void;
  location: SignupFormLocation;
  onLastStepSubmitted?: () => void;
}): Promise<{
  success: boolean;
  resolvedDiscounts?: Record<string, string>;
}> => {
  if (!spotlight || !spotlightId || !client) {
    return { success: false };
  }

  const transformedFormData: SignupFormData = {};
  const customFields: {
    declarationId: string;
    dataType: CustomerCustomFieldDataType;
    value: CustomerCustomFieldValue;
  }[] = [];

  for (const [key, value] of Object.entries(formData)) {
    if (key === "email" || key === "phone") {
      transformedFormData[key] = value;
    } else if (key === "birthday") {
      const [month, day, year] = value.split("-").map((x) => parseInt(x));
      transformedFormData.birthday = new Date(
        Temporal.PlainDate.from({ year, month, day }).toString(),
      );
    }
  }

  const processCustomFieldInputElement = (
    element: InputElement,
    formData: Record<string, string>,
    customFields: Array<{
      declarationId: string;
      dataType: CustomerCustomFieldDataType;
      value: CustomerCustomFieldValue;
    }>,
  ) => {
    if (!element.customField) {
      return;
    }
    const rawValue = formData[element.customField.id];
    // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
    let processedValue: CustomerCustomFieldValue = rawValue;
    switch (element.customField.fieldType) {
      case CustomFieldType.Number:
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        processedValue = parseFloat(rawValue);
        break;
      case CustomFieldType.Date:
        try {
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          const [month, day, year] = rawValue
            .split("-")
            .map((x) => parseInt(x));
          processedValue = new Date(
            Temporal.PlainDate.from({ year, month, day }).toString(),
          );
        } catch (e) {
          console.error("Failed to parse date for custom field", e);
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          processedValue = rawValue;
        }
        break;
      case CustomFieldType.YesNoQuestion:
        processedValue =
          // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
          rawValue.toLowerCase() === "yes" || rawValue.toLowerCase() === "true";
        break;
      default:
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        processedValue = rawValue;
    }

    customFields.push({
      declarationId: element.customField.id,
      dataType: customFieldTypeToDataType[element.customField.fieldType],
      value: processedValue,
    });
  };

  const currentStep = spotlight.steps[i];
  if (currentStep) {
    for (const element of currentStep.elements) {
      if (element.type === MarketingForgeElementType.COLUMN) {
        for (const columnElement of element.elements) {
          if (
            columnElement.type === MarketingForgeElementType.INPUT &&
            columnElement.customField &&
            columnElement.customField.id &&
            formData[columnElement.customField.id]
          ) {
            processCustomFieldInputElement(
              columnElement,
              formData,
              customFields,
            );
          }
        }
      } else if (
        element.type === MarketingForgeElementType.INPUT &&
        element.customField &&
        element.customField.id &&
        formData[element.customField.id]
      ) {
        processCustomFieldInputElement(element, formData, customFields);
      }
    }
  }

  if (customFields.length > 0) {
    transformedFormData.customFields = customFields;
  }

  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  const stepId = currentStep._id;

  const result = await client.submitSpotlightStep({
    spotlightId,
    stepId,
    formData: transformedFormData,
    customerId: customerId ?? undefined,
    anonymousId: localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY) ?? undefined,
    treatmentId,
    location,
  });

  if (!result.success) {
    console.error("Failed to submit spotlight step", result);
    return { success: false };
  }

  const anonymousId = localStorage.getItem(ANONYMOUS_SHOPPER_ID_KEY);

  if (client && spotlightId && anonymousId) {
    await client.logSpotlightAnalytics({
      anonymousId,
      spotlightId,
      treatmentId,
      type: "submit",
      shopifyCustomerId,
    });

    if (isCompletionStep(spotlight, i)) {
      await client.logSpotlightAnalytics({
        anonymousId,
        spotlightId,
        treatmentId,
        type: "complete",
        shopifyCustomerId,
      });
    }
  }

  const nextStepIndex = getNextStepIndex(spotlight, i + 1);
  const hasNextStep = nextStepIndex < spotlight.steps.length;

  if (hasNextStep) {
    setCurrentStep(nextStepIndex);
  } else {
    setCurrentStep(0);
    onLastStepSubmitted?.();
  }

  return { success: true, resolvedDiscounts: result.resolvedDiscounts };
};

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    const item = localStorage.getItem(key);
    return item ? (parseLocalData(item) as T) : initialValue;
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(storedValue));
  }, [key, storedValue]);

  return [storedValue, setStoredValue];
}
